import React, { useState, useRef } from "react";
import styled from "styled-components";
import { TitleText } from "./Text";
import { DropDownIcon } from "../assets/svg/DropDownIcon";
import { IndicatorIcon } from "../assets/svg/IndicatorIcon";
import { EditDeleteFormatter } from "./TableFormatter";
import CommentIcon from "../assets/svg/CommentIcon";
import { StatusIcon } from "../assets/svg/StatusIcon";
import { get } from "../utils/get";
import { is360Subject } from "../subjects/Is360Subject";

interface IProps {
  kpi?: any;
  isConfig?: boolean;
  showEdit?: any;
  handleDelete?: any;
  hasComments?: any;
  toggleComment?: any;
  isActive?: boolean;
}

const Accordion = ({
  kpi,
  isConfig,
  showEdit,
  handleDelete,
  toggleComment,
  hasComments,
  isActive,
}: IProps) => {
  const [setActive, setActiveState] = useState("");
  const [setHeight, setHeightState] = useState("0px");
  const [, setRotateState] = useState("accordion__icon");

  const content = useRef<any>(null);

  const is360 = !is360Subject.value;

  function toggleAccordion() {
    setActiveState(setActive === "" ? "active" : "");
    setHeightState(
      setActive === "active" ? "0px" : `${content.current.scrollHeight}px`
    );
  }
  return (
    <AccordionContainer>
      <div key={kpi.id}>
        <AccordionButton>
          <AccordionItem onClick={() => is360 && toggleAccordion()}>
            <Indicator>
              <IndicatorIcon color="#FCB444" />
            </Indicator>
            <TitleText style={{ fontSize: "12px" }}>
              {isConfig ? kpi?.name : kpi?.kpi.name}
            </TitleText>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: "auto",
              }}
            >
              {!isConfig && (
                <TitleText
                  style={{
                    fontSize: "12px",
                    marginRight: "1em",
                    color: "#D51067",
                    fontWeight: "normal",
                  }}
                >
                  {kpi.weight}
                </TitleText>
              )}
              <AccordionIcon>{is360 && <DropDownIcon />}</AccordionIcon>
            </div>
          </AccordionItem>
          {hasComments && get(kpi, "comments")?.length > 0 ? (
            <CommentIconContainer onClick={() => toggleComment("comment", kpi)}>
              <StatusContainer>
                <StatusIcon />
              </StatusContainer>
              <CommentIcon />
            </CommentIconContainer>
          ) : null}
          <EditDeleteFormatter
            showEdit={() => showEdit(kpi)}
            handleDelete={handleDelete}
            isActive={isActive}
            hasComments={hasComments}
            toggleComment={() => toggleComment("comment", kpi)}
          />
        </AccordionButton>
        {is360 && (
          <AccordionContent ref={content} style={{ maxHeight: `${setHeight}` }}>
            <Description>{kpi.notes}</Description>
            <CardContainer>
              <CardItem>
                <Title>Type</Title>
                <TitleText>
                  {isConfig ? kpi.kpi_type : kpi.kpi.kpi_type}
                </TitleText>
              </CardItem>
              <CardItem>
                <Title>Scoring</Title>
                <TitleText>
                  {isConfig ? kpi.score_type : kpi.kpi.score_type}
                </TitleText>
              </CardItem>

              <CardItem>
                <Title>Optimisation</Title>
                <TitleText>
                  {isConfig ? kpi.optimisation : kpi.kpi.optimisation}
                </TitleText>
              </CardItem>
              <CardItem>
                <Title>Weight</Title>
                <TitleText>{kpi.weight}</TitleText>
              </CardItem>
              <CardItem>
                <Title>Target</Title>
                <TitleText>{kpi.target}</TitleText>
              </CardItem>
              {!isConfig ? (
                <CardItem>
                  <Title>Target Type</Title>
                  <TitleText>{kpi.kpi_target_type}</TitleText>
                </CardItem>
              ) : null}
              {!isConfig ? (
                <CardItem>
                  <Title>Normalized Weight </Title>
                  <TitleText>{kpi?.normalised_weight}</TitleText>
                </CardItem>
              ) : null}
            </CardContainer>
          </AccordionContent>
        )}
      </div>
    </AccordionContainer>
  );
};

export default Accordion;

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 0.5em;
`;
const AccordionButton = styled.div`
  display: flex;
  align-items: center;
`;
const AccordionItem = styled.button`
  background-color: #f5f6fb;
  color: #444;
  cursor: pointer;
  padding: 0.75em;
  display: flex;
  align-items: center;
  margin: 1em;
  width: 90%;
  border: none;
  border-radius: 1em;
  outline: none;
  transition: background-color 0.6s ease;
`;

const AccordionIcon = styled.span`
  margin-left: auto;
  transition: transform 0.6s ease;
  padding-right: 1em;
`;

const AccordionContent = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  background-color: white;
  overflow: hidden;
  transition: max-height 0.6s ease;
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: 1em;
  row-gap: 1em;
  margin: 1em;
`;

const CardItem = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f5f6fb;
  padding: 1em;
  border-radius: 1em;
`;

const Description = styled.p`
  font-size: 0.75em;
  color: #214160;
  opacity: 0.5;
  margin: 1em;
  padding: 0.75em;
  line-height: 18px;
`;

const Title = styled.h5`
  margin: 0;
  font-size: 0.75em;
  font-weight: 450;
  color: #214160;
  opacity: 0.5;
  margin-bottom: 1em;
`;

const Indicator = styled.span`
  padding-right: 1em;
`;

const CommentIconContainer = styled.span`
  position: relative;
  height: 1em;
  width: 1em;
  margin: 0.3em;
  display: flex;
  align-items: center;
  padding: 0.5em;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #ccc;
  cursor: pointer;
`;

const StatusContainer = styled.div`
  position: absolute;
  bottom: 1.4em;
  left: 1.5em;
`;
